// Shared API contracts: input schemas (validated on the server, reused by the client) and response types.
import { z } from 'zod';

export * from './editorial.ts';
export * from './calendar.ts';
export * from './pages.ts';
export * from './crm.ts';
export * from './finance.ts';
export * from './hr.ts';
export * from './chat.ts';
export * from './tickets.ts';
export * from './social.ts';
export * from './developer.ts';

// ---------------------------------------------------------------------------
// Primitives
// ---------------------------------------------------------------------------
export const Uuid = z.string().uuid();
export const Email = z.string().trim().toLowerCase().email('Email non valida').max(254);
export const Password = z
  .string()
  .min(12, 'La password deve avere almeno 12 caratteri')
  .max(200, 'La password è troppo lunga');
export const PersonName = z.string().trim().min(1, 'Inserisci il nome').max(120);
export const DateOnly = z.string().regex(/^\d{4}-\d{2}-\d{2}$/, 'Data non valida');
export const TimeOfDay = z.string().regex(/^([01]\d|2[0-3]):[0-5]\d$/, 'Ora non valida');

export const ROLE_KEYS = ['owner', 'admin', 'manager', 'member', 'junior', 'guest'] as const;
export const RoleKey = z.enum(ROLE_KEYS);
export type RoleKey = z.infer<typeof RoleKey>;

export const ROLE_LABELS: Record<RoleKey, string> = {
  owner: 'Owner',
  admin: 'Admin',
  manager: 'Manager',
  member: 'Membro',
  junior: 'Junior',
  guest: 'Ospite',
};

/** Plain-language summary of what each role can see/fare, shown wherever an admin decides who gets which role. */
export const ROLE_DESCRIPTIONS: Record<RoleKey, string> = {
  owner: 'Accesso completo a tutto, inclusi fatturazione e trasferimento della proprietà.',
  admin: 'Accesso completo a tutti gli spazi e alle impostazioni dell’organizzazione, inviti e membri compresi.',
  manager: 'Vede e modifica tutto negli spazi di cui fa parte; può creare ed eliminare elementi.',
  member: 'Vede e modifica tutto negli spazi di cui fa parte, ma non può eliminare né gestire i permessi.',
  junior: 'Negli spazi di cui fa parte vede solo ciò che gli è assegnato o che ha creato lui stesso.',
  guest: 'Come Junior, ma pensato per collaboratori esterni: non vede l’elenco membri né le impostazioni.',
};

export const WORKSPACE_TYPES = ['general', 'media', 'department', 'client'] as const;
export const WorkspaceType = z.enum(WORKSPACE_TYPES);
export type WorkspaceType = z.infer<typeof WorkspaceType>;
export const WORKSPACE_TYPE_LABELS: Record<WorkspaceType, string> = {
  general: 'Generale',
  media: 'Media',
  department: 'Reparto',
  client: 'Cliente',
};

export const WORKSPACE_COLORS = ['indigo', 'blue', 'teal', 'green', 'lime', 'amber', 'orange', 'red', 'pink', 'purple'] as const;
export const WorkspaceColor = z.enum(WORKSPACE_COLORS);
export type WorkspaceColor = z.infer<typeof WorkspaceColor>;

/** 0 = nessuna, 1 = urgente, 2 = alta, 3 = media, 4 = bassa (ordinamento stile Linear). */
export const Priority = z.number().int().min(0).max(4);
export const PRIORITY_LABELS = ['Nessuna', 'Urgente', 'Alta', 'Media', 'Bassa'] as const;

// ---------------------------------------------------------------------------
// Auth & account
// ---------------------------------------------------------------------------
export const SignupInput = z.object({
  name: PersonName,
  email: Email,
  password: Password,
  organizationName: z.string().trim().min(1, "Inserisci il nome dell'azienda").max(120),
});
export type SignupInput = z.infer<typeof SignupInput>;

export const LoginInput = z.object({
  email: Email,
  password: z.string().min(1, 'Inserisci la password').max(200),
});
export type LoginInput = z.infer<typeof LoginInput>;

export const UpdateMeInput = z.object({
  displayName: PersonName.optional(),
  timeZone: z.string().min(1).max(64).optional(),
});
export type UpdateMeInput = z.infer<typeof UpdateMeInput>;

export const AcceptInviteInput = z.object({
  name: PersonName.optional(),
  password: Password.optional(),
});
export type AcceptInviteInput = z.infer<typeof AcceptInviteInput>;

export const ChangePasswordInput = z.object({
  currentPassword: z.string().min(1, 'Inserisci la password attuale').max(200),
  newPassword: Password,
});
export type ChangePasswordInput = z.infer<typeof ChangePasswordInput>;

export const ForgotPasswordInput = z.object({ email: Email });
export type ForgotPasswordInput = z.infer<typeof ForgotPasswordInput>;

export const ResetPasswordInput = z.object({
  token: z.string().min(1),
  password: Password,
});
export type ResetPasswordInput = z.infer<typeof ResetPasswordInput>;

export const VerifyEmailInput = z.object({ code: z.string().trim().length(6).regex(/^\d{6}$/, 'Inserisci il codice a 6 cifre') });
export type VerifyEmailInput = z.infer<typeof VerifyEmailInput>;

// ---------------------------------------------------------------------------
// Organization
// ---------------------------------------------------------------------------
export const UpdateOrgInput = z.object({
  name: z.string().trim().min(1).max(120),
});

export const InviteInput = z.object({
  email: Email,
  role: RoleKey.exclude(['owner']),
  /** Optional workspace to join; new members always join the organization's general workspaces. */
  workspaceId: Uuid.optional(),
});
export type InviteInput = z.infer<typeof InviteInput>;

export const UpdateMemberInput = z.object({
  role: RoleKey.exclude(['owner']),
});

// ---------------------------------------------------------------------------
// Workspaces
// ---------------------------------------------------------------------------
export const WorkspaceKey = z
  .string()
  .trim()
  .toUpperCase()
  .regex(/^[A-Z][A-Z0-9]{1,9}$/, 'Sigla: 2–10 lettere o numeri, inizia con una lettera');

export const CreateWorkspaceInput = z.object({
  name: z.string().trim().min(1, 'Inserisci un nome').max(80),
  key: WorkspaceKey,
  type: WorkspaceType.default('general'),
  color: WorkspaceColor.default('indigo'),
});
export type CreateWorkspaceInput = z.infer<typeof CreateWorkspaceInput>;

export const UpdateWorkspaceInput = CreateWorkspaceInput.omit({ key: true }).partial();

export const WorkspaceMemberInput = z.object({ userId: Uuid });

// ---------------------------------------------------------------------------
// Tasks
// ---------------------------------------------------------------------------
export const CreateTaskInput = z.object({
  workspaceId: Uuid,
  title: z.string().trim().min(1, 'Scrivi un titolo').max(500),
  description: z.string().max(20000).optional(),
  priority: Priority.default(0),
  dueDate: DateOnly.nullable().optional(),
  dueTime: TimeOfDay.nullable().optional(),
  assigneeIds: z.array(Uuid).max(20).default([]),
  stateId: Uuid.optional(),
});
export type CreateTaskInput = z.input<typeof CreateTaskInput>;

export const UpdateTaskInput = z
  .object({
    title: z.string().trim().min(1, 'Scrivi un titolo').max(500),
    description: z.string().max(20000),
    priority: Priority,
    dueDate: DateOnly.nullable(),
    dueTime: TimeOfDay.nullable(),
    assigneeIds: z.array(Uuid).max(20),
    stateId: Uuid,
    workspaceId: Uuid,
  })
  .partial();
export type UpdateTaskInput = z.infer<typeof UpdateTaskInput>;

export const TaskListQuery = z.object({
  scope: z.enum(['mine', 'all']).default('mine'),
  workspaceId: Uuid.optional(),
  status: z.enum(['open', 'done', 'all']).default('open'),
  due: z.enum(['any', 'today', 'overdue', 'upcoming', 'none']).default('any'),
  limit: z.coerce.number().int().min(1).max(200).default(200),
});
export type TaskListQuery = z.input<typeof TaskListQuery>;

export const CommentInput = z.object({
  body: z.string().trim().min(1, 'Il commento è vuoto').max(10000),
});

export const MarkReadInput = z.object({
  ids: z.array(Uuid).max(200).optional(),
  all: z.boolean().optional(),
});

export const SearchQuery = z.object({
  q: z.string().trim().min(1).max(100),
});

// ---------------------------------------------------------------------------
// Response types
// ---------------------------------------------------------------------------
export interface UserRef {
  id: string;
  displayName: string;
  /** Not populated everywhere yet — treat as null when absent (falls back to initials). */
  avatarUrl?: string | null;
}

export interface OrgSummary {
  id: string;
  slug: string;
  name: string;
  role: RoleKey;
}

export interface Me {
  user: { id: string; email: string; displayName: string; timeZone: string; locale: string; avatarUrl: string | null; emailVerified: boolean };
  organizations: OrgSummary[];
}

export interface MyOrgProfile {
  nickname: string | null;
}

export interface OrgDetail extends OrgSummary {
  status: string;
  capabilities: {
    admin: boolean;
    invite: boolean;
    createWorkspace: boolean;
  };
}

export interface Member {
  userId: string;
  displayName: string;
  email: string;
  avatarUrl: string | null;
  nickname: string | null;
  role: RoleKey;
  seatType: 'full' | 'guest';
  status: string;
  joinedAt: string;
}

export interface Invitation {
  id: string;
  email: string;
  role: RoleKey;
  expiresAt: string;
  createdAt: string;
  /** Returned only once, at creation: the token is stored hashed. */
  inviteUrl?: string;
}

export interface InvitationPreview {
  organizationName: string;
  email: string;
  role: RoleKey;
  accountExists: boolean;
}

export interface Workspace {
  id: string;
  key: string;
  name: string;
  type: WorkspaceType;
  color: WorkspaceColor;
  isMember: boolean;
  openTasks: number;
  myOpenTasks: number;
  myAttentionTasks: number;
  canManage: boolean;
  canDelete: boolean;
  logoUrl: string | null;
  pedEnabled: boolean;
  crmEnabled: boolean;
  financeEnabled: boolean;
  hrEnabled: boolean;
  ticketsEnabled: boolean;
  /** Tabs the creator/an org admin has chosen to hide from everyone else (never includes 'tasks'). */
  hiddenModules: HidableModule[];
  /** Tabs hidden from the caller specifically (on top of hiddenModules above) - see workspace_members.hidden_modules. */
  myHiddenModules: HidableModule[];
  createdBy: string;
}

export const HIDABLE_MODULES = ['ped', 'crm', 'finance', 'hr', 'tickets', 'documents', 'people', 'insights', 'content', 'marketing'] as const;
export type HidableModule = (typeof HIDABLE_MODULES)[number];
export const SetModuleVisibilityInput = z.object({ module: z.enum(HIDABLE_MODULES), hidden: z.boolean() });
export type SetModuleVisibilityInput = z.infer<typeof SetModuleVisibilityInput>;

/** A workspace member's custom display title (e.g. "Copywriter") and personal tab visibility -
 * set by whoever manages the workspace, from its Persone tab. Neither field grants or removes any
 * permission: position is purely cosmetic, and hiddenModules only declutters that person's own tabs. */
export interface WorkspaceMember extends Member {
  position: string | null;
  hiddenModules: HidableModule[];
}
export const WorkspaceMemberSettingsInput = z.object({
  position: z.string().trim().max(60).nullable(),
  hiddenModules: z.array(z.enum(HIDABLE_MODULES)),
});
export type WorkspaceMemberSettingsInput = z.infer<typeof WorkspaceMemberSettingsInput>;

export const TransferWorkspaceInput = z.object({ userId: Uuid });
export type TransferWorkspaceInput = z.infer<typeof TransferWorkspaceInput>;

// ------------------------------------------------------------------ custom workspace sections
export const WorkspaceSectionInput = z.object({
  name: z.string().trim().min(1, 'Scrivi un nome').max(40),
  icon: z.string().trim().min(1).max(8).default('📁'),
});
export type WorkspaceSectionInput = z.input<typeof WorkspaceSectionInput>;

export interface WorkspaceSection {
  id: string;
  workspaceId: string;
  name: string;
  icon: string;
  position: number;
}

// ------------------------------------------------------------------ section content blocks
// Free-form content inside a custom section, Notion-style: text (collapsible, with a computed
// reading time), a simple table, or an embedded video link. `content`'s shape depends on `type`.
export const SectionBlockType = z.enum(['text', 'table', 'video']);
export type SectionBlockType = z.infer<typeof SectionBlockType>;

export const TextBlockContent = z.object({ body: z.string().trim().min(1, 'Scrivi qualcosa').max(20000) });
export const TableBlockContent = z.object({
  columns: z.array(z.string().trim().max(60)).min(1, 'Aggiungi almeno una colonna').max(12),
  rows: z.array(z.array(z.string().max(500))).max(300),
});
export const VideoBlockContent = z.object({
  url: z.string().trim().url('Inserisci un link valido').max(600),
  caption: z.string().trim().max(200).nullable().optional(),
});

export const SectionBlockInput = z.object({
  type: SectionBlockType,
  title: z.string().trim().max(80).nullable().optional(),
  content: z.unknown(),
});
export type SectionBlockInput = z.infer<typeof SectionBlockInput>;

export const SectionBlockUpdateInput = z.object({
  title: z.string().trim().max(80).nullable().optional(),
  content: z.unknown().optional(),
});
export type SectionBlockUpdateInput = z.infer<typeof SectionBlockUpdateInput>;

export interface SectionBlock {
  id: string;
  sectionId: string;
  type: SectionBlockType;
  title: string | null;
  content: Record<string, unknown>;
  position: number;
  /** Words-per-minute estimate; only set for `type: 'text'`. */
  readingTimeMinutes: number | null;
  createdAt: string;
  updatedAt: string;
}

export type StateCategory = 'backlog' | 'todo' | 'in_progress' | 'review' | 'scheduled' | 'done' | 'canceled';

export interface TaskState {
  id: string;
  name: string;
  category: StateCategory;
  position: number;
}

export interface Task {
  id: string;
  key: string;
  workspaceId: string;
  workspaceKey: string;
  workspaceName: string;
  workspaceColor: WorkspaceColor;
  title: string;
  description: string;
  state: { id: string; name: string; category: StateCategory };
  priority: number;
  dueDate: string | null;
  dueTime: string | null;
  isOverdue: boolean;
  completedAt: string | null;
  assignees: UserRef[];
  createdBy: UserRef;
  createdAt: string;
  updatedAt: string;
  commentCount: number;
  permissions: { edit: boolean; delete: boolean; comment: boolean };
}

export interface Comment {
  id: string;
  author: UserRef;
  body: string;
  createdAt: string;
  editedAt: string | null;
  mine: boolean;
}

export interface Notification {
  id: string;
  kind: string;
  title: string;
  body: string | null;
  entityType: string | null;
  entityId: string | null;
  actor: UserRef | null;
  readAt: string | null;
  createdAt: string;
}

export interface ActivityItem {
  id: string;
  action: string;
  actor: UserRef | null;
  entityType: string | null;
  entityId: string | null;
  text: string;
  occurredAt: string;
}

export interface HomePayload {
  firstName: string;
  counts: { today: number; overdue: number; upcoming: number; unread: number };
  today: Task[];
  overdue: Task[];
  upcoming: Task[];
  workspaces: Workspace[];
  activity: ActivityItem[];
}

export interface Session {
  id: string;
  deviceLabel: string;
  ip: string | null;
  createdAt: string;
  lastSeenAt: string;
  current: boolean;
}

export interface SearchResult {
  type: 'task' | 'workspace' | 'member' | 'page' | 'crm_company' | 'crm_contact' | 'deal' | 'ticket';
  id: string;
  title: string;
  subtitle: string;
  color?: WorkspaceColor;
  /** Workspace to navigate to for types that only open inside a workspace tab (no global detail view yet). */
  workspaceKey?: string;
  /** Which workspace tab to land on for those types (e.g. 'crm', 'tickets', 'documents'). */
  tab?: string;
}

// ---------------------------------------------------------------------------
// Preferences (generic per-user JSON store) & customizable Home layout
// ---------------------------------------------------------------------------
export const PreferenceValueInput = z.object({ value: z.unknown() });

/** A browser's PushSubscription (subscription.toJSON()), sent when device notifications are turned on. */
export const PushSubscriptionInput = z.object({
  endpoint: z.string().url().max(1000),
  keys: z.object({ p256dh: z.string().min(1).max(200), auth: z.string().min(1).max(100) }),
});
export type PushSubscriptionInput = z.infer<typeof PushSubscriptionInput>;
export const PushEndpointInput = z.object({ endpoint: z.string().url().max(1000) });
export interface PushStatus {
  /** False until the server has VAPID keys: the setting then says it isn't available yet. */
  configured: boolean;
  publicKey: string | null;
  /** Whether the endpoint asked about is turned on for this person in this organization. */
  subscribed: boolean;
}

export const HOME_SECTION_IDS = ['stories', 'overdue', 'today', 'upcoming', 'activity', 'notifications'] as const;
export type HomeSectionId = (typeof HOME_SECTION_IDS)[number];
export const HOME_SECTION_LABELS: Record<HomeSectionId, string> = {
  stories: 'Spazi',
  overdue: 'In ritardo',
  today: 'Oggi',
  upcoming: 'Prossimi 7 giorni',
  activity: 'Attività del team',
  notifications: 'Notifiche recenti',
};
export const HOME_SECTION_DESCRIPTIONS: Record<HomeSectionId, string> = {
  stories: 'I tuoi spazi, come cerchi da toccare',
  overdue: 'Le task scadute non ancora completate',
  today: 'Le task in scadenza oggi',
  upcoming: 'Le task dei prossimi 7 giorni',
  activity: 'Cosa succede nel team: task, contenuti, spazi',
  notifications: 'Le ultime notifiche ricevute',
};

export const HomeLayoutSectionInput = z.object({
  id: z.enum(HOME_SECTION_IDS), visible: z.boolean(),
  width: z.number().int().min(1).max(4).optional(), compactWidth: z.number().int().min(1).max(2).optional(),
});
export const HomeLayoutInput = z.object({
  sections: z.array(HomeLayoutSectionInput).min(1).max(HOME_SECTION_IDS.length),
});
/** Grid columns: the Home is a 4-column grid on desktop and a 2-column one on tablet and phone. */
export type HomeWidth = 1 | 2 | 3 | 4;
export type HomeCompactWidth = 1 | 2;
export interface HomeLayoutSection {
  id: HomeSectionId;
  visible: boolean;
  /** Columns out of 4 on desktop. */
  width: HomeWidth;
  /** Columns out of 2 on tablet and phone. */
  compactWidth: HomeCompactWidth;
}

/** Default spans, matching the old two-column Home (wide left, narrow right). */
export const HOME_SECTION_WIDTHS: Record<HomeSectionId, HomeWidth> = {
  stories: 4, overdue: 2, today: 2, upcoming: 2, activity: 2, notifications: 2,
};
export interface HomeLayout {
  sections: HomeLayoutSection[];
}

export const DEFAULT_HOME_LAYOUT: HomeLayout = {
  sections: HOME_SECTION_IDS.map((id) => ({ id, visible: id !== 'notifications', width: HOME_SECTION_WIDTHS[id], compactWidth: 2 })),
};

/** Layout presets tuned for how different team types actually work day to day, not just "more/fewer widgets". */
export const HOME_TEMPLATES: { id: string; name: string; description: string; sections: HomeSectionId[] }[] = [
  { id: 'essential', name: 'Essenziale', description: 'Solo il necessario per la giornata', sections: ['today', 'overdue'] },
  { id: 'editorial', name: 'Redazione', description: 'Spazi, scadenze e prossimi contenuti in evidenza', sections: ['stories', 'today', 'upcoming', 'overdue'] },
  { id: 'social', name: 'Social & Community', description: 'Oggi e notifiche in primo piano, per rispondere in fretta', sections: ['today', 'notifications', 'stories'] },
  { id: 'sales', name: 'Vendite', description: 'Scadenze e attività del team sempre sotto controllo', sections: ['today', 'overdue', 'activity'] },
  { id: 'management', name: 'Management', description: 'Visione completa su spazi, scadenze e attività del team', sections: ['stories', 'overdue', 'today', 'upcoming', 'activity'] },
  { id: 'operations', name: 'Operations', description: 'Coda di lavoro pulita: solo scadenze e prossimi impegni', sections: ['overdue', 'today', 'upcoming'] },
  { id: 'complete', name: 'Completa', description: 'Tutte le sezioni disponibili', sections: [...HOME_SECTION_IDS] },
];

/** Merge a saved layout with the current known sections: keeps the saved order, appends any new section id (visible) at the end, drops unknown ids. */
export function normalizeHomeLayout(raw: unknown): HomeLayout {
  const saved = raw && typeof raw === 'object' && Array.isArray((raw as HomeLayout).sections) ? (raw as HomeLayout).sections : [];
  const known = new Set(HOME_SECTION_IDS);
  const seen = new Set<HomeSectionId>();
  const sections: HomeLayoutSection[] = [];
  for (const s of saved) {
    if (s && known.has(s.id) && !seen.has(s.id)) {
      seen.add(s.id);
      const width = [1, 2, 3, 4].includes(s.width) ? s.width : HOME_SECTION_WIDTHS[s.id];
      const compactWidth = s.compactWidth === 1 ? 1 : 2;
      sections.push({ id: s.id, visible: Boolean(s.visible), width, compactWidth });
    }
  }
  for (const d of DEFAULT_HOME_LAYOUT.sections) if (!seen.has(d.id)) sections.push({ ...d });
  return { sections };
}

/** Per-person shelf of pinned/ordered workspaces (stored as a generic preference, key "workspace_shelf"). */
export interface WorkspaceShelf {
  /** Up to 3 workspace ids, shown first and separately. */
  pinned: string[];
  /** Custom display order for the rest (and, within it, ties for pinned order too). */
  order: string[];
}
export const MAX_PINNED_WORKSPACES = 3;

/** Merge a saved shelf with the workspaces the member can currently see: drops ids that no longer
 * exist/are visible, keeps the saved order, appends any new workspace id at the end. */
export function normalizeWorkspaceShelf(raw: unknown, workspaceIds: string[]): WorkspaceShelf {
  const known = new Set(workspaceIds);
  const savedPinned = raw && typeof raw === 'object' && Array.isArray((raw as WorkspaceShelf).pinned) ? (raw as WorkspaceShelf).pinned : [];
  const savedOrder = raw && typeof raw === 'object' && Array.isArray((raw as WorkspaceShelf).order) ? (raw as WorkspaceShelf).order : [];
  const pinned = [...new Set(savedPinned.filter((id) => known.has(id)))].slice(0, MAX_PINNED_WORKSPACES);
  const orderSeen = new Set<string>();
  const order: string[] = [];
  for (const id of savedOrder) if (known.has(id) && !orderSeen.has(id)) { orderSeen.add(id); order.push(id); }
  for (const id of workspaceIds) if (!orderSeen.has(id)) order.push(id);
  return { pinned, order };
}

/** Applies a shelf to a list of workspaces: pinned ones first (in shelf order), then the rest in shelf order. */
export function applyWorkspaceShelf<T extends { id: string }>(items: T[], shelf: WorkspaceShelf): T[] {
  const byId = new Map(items.map((w) => [w.id, w]));
  const pinned = shelf.pinned.map((id) => byId.get(id)).filter((w): w is T => Boolean(w));
  const pinnedIds = new Set(shelf.pinned);
  const rest = shelf.order.filter((id) => !pinnedIds.has(id)).map((id) => byId.get(id)).filter((w): w is T => Boolean(w));
  return [...pinned, ...rest];
}

/** Per-person email notification toggles (stored as a generic preference, key "email_notifications"). */
export interface EmailNotificationPrefs {
  taskAssigned: boolean;
  dailyDigest: boolean;
  chat: boolean;
}
export const DEFAULT_EMAIL_NOTIFICATION_PREFS: EmailNotificationPrefs = { taskAssigned: true, dailyDigest: true, chat: true };

/** Merge a saved (possibly partial or empty) value with the defaults, so a never-configured or
 * older-shape preference still turns into a complete set of toggles. */
export function normalizeEmailNotificationPrefs(raw: unknown): EmailNotificationPrefs {
  const saved = raw && typeof raw === 'object' ? (raw as Partial<EmailNotificationPrefs>) : {};
  return {
    taskAssigned: typeof saved.taskAssigned === 'boolean' ? saved.taskAssigned : DEFAULT_EMAIL_NOTIFICATION_PREFS.taskAssigned,
    dailyDigest: typeof saved.dailyDigest === 'boolean' ? saved.dailyDigest : DEFAULT_EMAIL_NOTIFICATION_PREFS.dailyDigest,
    chat: typeof saved.chat === 'boolean' ? saved.chat : DEFAULT_EMAIL_NOTIFICATION_PREFS.chat,
  };
}

export interface Problem {
  type: string;
  title: string;
  status: number;
  code: string;
  detail?: string;
  errors?: Record<string, string>;
  requestId?: string;
}
